/**
 * Smart Scheduler engine.
 *
 * Pure scheduling logic — no React, no localStorage, no side effects on the
 * shared store. Inputs are the tournament + its divisions/teams/rinks/settings;
 * the engine returns proposed games, proposed slots, and human-readable warnings.
 *
 * Strategy (Ironman): each division plays a single round-robin; matches are
 * dealt onto rinks in time order, respecting min team rest and rink turnover
 * gaps. We greedily pick the next match that won't violate the rest constraint;
 * when forced, we relax it and emit a warning.
 */

import type {
  Division,
  Game,
  Id,
  Iso8601,
  Rink,
  ScheduleSlot,
  Team,
  Tournament,
} from '../models';

export interface ScheduleInput {
  tournament: Tournament;
  divisions: Division[];
  teams: Team[];
  rinks: Rink[];
}

export type SchedulerWarningCode =
  | 'forced_back_to_back'
  | 'long_gap'
  | 'late_finish'
  | 'odd_team_count'
  | 'no_rinks'
  | 'no_teams';

export interface SchedulerWarning {
  code: SchedulerWarningCode;
  message: string;
  /** Optional context for the UI (team id, division id, etc.). */
  context?: Record<string, string | number>;
}

export interface ProposedGame extends Omit<Game, 'kind' | 'createdAt' | 'updatedAt'> {
  /** Slot id in the same proposal. */
  scheduleSlotId: Id;
}

export interface ProposedSlot extends Omit<ScheduleSlot, 'kind' | 'createdAt' | 'updatedAt'> {}

export interface ScheduleProposal {
  games: ProposedGame[];
  slots: ProposedSlot[];
  warnings: SchedulerWarning[];
  stats: {
    perTeam: Record<Id, { gameCount: number; longestGapMinutes: number; backToBacks: number }>;
  };
}

/** Round-robin pairings for a list of teams (each pair once). */
export function roundRobinPairings<T>(teams: T[]): Array<[T, T]> {
  const pairs: Array<[T, T]> = [];
  for (let i = 0; i < teams.length; i++) {
    for (let j = i + 1; j < teams.length; j++) {
      pairs.push([teams[i], teams[j]]);
    }
  }
  return pairs;
}

interface TeamState {
  lastEndAt: number; // epoch ms
  firstStartAt: number;
  gameCount: number;
  backToBacks: number;
  longestGap: number;
  endHistory: number[];
}

const id = (prefix: string) => `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

export function generateSchedule(input: ScheduleInput): ScheduleProposal {
  const { tournament, divisions, teams, rinks } = input;
  const warnings: SchedulerWarning[] = [];

  if (rinks.length === 0) {
    warnings.push({ code: 'no_rinks', message: 'No rinks available — add at least one rink.' });
    return { games: [], slots: [], warnings, stats: { perTeam: {} } };
  }
  if (teams.length === 0) {
    warnings.push({ code: 'no_teams', message: 'No teams to schedule yet.' });
    return { games: [], slots: [], warnings, stats: { perTeam: {} } };
  }

  const gameLen = tournament.settings.scoring.gameLengthMinutes;
  const turnover = tournament.settings.scheduling.rinkTurnoverMinutes;
  const minRest = tournament.settings.scheduling.minTeamRestMinutes;

  // Build a flat queue of matches grouped by division.
  type Match = { divisionId: Id; home: Team; away: Team };
  const queue: Match[] = [];
  for (const div of divisions) {
    const ts = teams.filter((t) => t.divisionId === div.id);
    if (ts.length < 2) continue;
    if (ts.length % 2 === 1) {
      warnings.push({
        code: 'odd_team_count',
        message: `Division "${div.name}" has an odd number of teams — one team will sit out each round.`,
        context: { divisionId: div.id },
      });
    }
    for (const [home, away] of roundRobinPairings(ts)) {
      queue.push({ divisionId: div.id, home, away });
    }
  }

  const tournamentStart = new Date(tournament.startsAt).getTime();
  const tournamentEnd = new Date(tournament.endsAt).getTime();
  const teamState = new Map<Id, TeamState>();
  const initState = (): TeamState => ({
    lastEndAt: 0,
    firstStartAt: 0,
    gameCount: 0,
    backToBacks: 0,
    longestGap: 0,
    endHistory: [],
  });

  // Timeline cursor per rink.
  const rinkCursor = new Map<Id, number>(rinks.map((r) => [r.id, tournamentStart]));

  const slots: ProposedSlot[] = [];
  const games: ProposedGame[] = [];

  // For each iteration, search across (rink × queued match) for the placement
  // with the earliest feasible start time. Both team concurrency AND minimum
  // team rest are treated as hard constraints — when honouring them would
  // require leaving a rink idle, that's the right answer for fairness.
  while (queue.length > 0) {
    let bestRinkId: Id | null = null;
    let bestIdx = -1;
    let bestStart = Number.POSITIVE_INFINITY;

    for (const [rinkId, rc] of rinkCursor.entries()) {
      for (let i = 0; i < queue.length; i++) {
        const m = queue[i];
        const home = teamState.get(m.home.id) ?? initState();
        const away = teamState.get(m.away.id) ?? initState();
        const homeReady = home.lastEndAt === 0 ? 0 : home.lastEndAt + minRest * 60_000;
        const awayReady = away.lastEndAt === 0 ? 0 : away.lastEndAt + minRest * 60_000;
        const candStart = Math.max(rc, homeReady, awayReady);
        if (candStart < bestStart) {
          bestStart = candStart;
          bestRinkId = rinkId;
          bestIdx = i;
        }
      }
    }

    if (bestRinkId === null || bestIdx < 0) break; // safety
    const rinkId = bestRinkId;
    const startMs = bestStart;
    const match = queue.splice(bestIdx, 1)[0];
    const startsAt: Iso8601 = new Date(startMs).toISOString();
    const endMs = startMs + gameLen * 60_000;
    const endsAt: Iso8601 = new Date(endMs).toISOString();

    const slot: ProposedSlot = {
      id: id('slot'),
      tournamentId: tournament.id,
      rinkId,
      startsAt,
      endsAt,
    };
    slots.push(slot);

    games.push({
      id: id('game'),
      tournamentId: tournament.id,
      divisionId: match.divisionId,
      scheduleSlotId: slot.id,
      homeTeamId: match.home.id,
      awayTeamId: match.away.id,
      status: 'scheduled',
      displayLabel: `${match.home.name} vs ${match.away.name}`,
    });

    // Update team state for both teams.
    for (const team of [match.home, match.away]) {
      const st = teamState.get(team.id) ?? initState();
      if (st.lastEndAt > 0) {
        const gap = (startMs - st.lastEndAt) / 60_000;
        st.longestGap = Math.max(st.longestGap, gap);
        if (gap < minRest) {
          st.backToBacks += 1;
          warnings.push({
            code: 'forced_back_to_back',
            message: `${team.name} plays with ${Math.round(gap)} min rest (below ${minRest}-min minimum).`,
            context: { teamId: team.id },
          });
        }
      } else {
        st.firstStartAt = startMs;
      }
      st.lastEndAt = endMs;
      st.gameCount += 1;
      st.endHistory.push(endMs);
      teamState.set(team.id, st);
    }

    // Advance cursor for this rink (game + turnover).
    rinkCursor.set(rinkId, endMs + turnover * 60_000);

    // Late-finish warning (one-shot).
    if (endMs > tournamentEnd && !warnings.some((w) => w.code === 'late_finish')) {
      warnings.push({
        code: 'late_finish',
        message: 'Schedule extends past the tournament end time — consider adding rinks or trimming games.',
      });
    }
  }

  // Long-gap warnings.
  for (const [teamId, st] of teamState.entries()) {
    if (st.longestGap > 180) {
      const team = teams.find((t) => t.id === teamId);
      warnings.push({
        code: 'long_gap',
        message: `${team?.name ?? 'Team'} has a ${Math.round(st.longestGap)}-minute gap between games.`,
        context: { teamId },
      });
    }
  }

  const perTeam: ScheduleProposal['stats']['perTeam'] = {};
  for (const [teamId, st] of teamState.entries()) {
    perTeam[teamId] = {
      gameCount: st.gameCount,
      longestGapMinutes: Math.round(st.longestGap),
      backToBacks: st.backToBacks,
    };
  }

  return { games, slots, warnings, stats: { perTeam } };
}
